/**
 * TanStack Query hooks for the Signals triage hub — the RM queue surfaced by
 * Suggested Intensity from Guidance (docs/specs/suggested-intensity-from-guidance.md).
 *
 * Response types are co-located here for the UI layer to import, per frontend.md.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  signalKeys,
  solutionDashboardKeys,
  solutionDetailKeys,
  solutionWorkloadKeys,
} from "@/lib/query";

// ── Types ──────────────────────────────────────────────────────────────────

export type SignalType =
  | "needs_guidance"
  | "guidance_change_pod_change"
  | "intensity_drift"
  | "resource_off_pod";

/**
 * No-black-box payload behind a Suggested-Intensity signal: the raw
 * forward-guidance (or trailing-actuals) months that fed the average, the
 * hours-per-Intensity-point divisor, the resulting average, and whether the
 * average had to be projected from history because no forward Guidance was on file.
 */
export interface IntensitySignalBasis {
  source: "guidance" | "history";
  months: (number | null)[];
  divisor: number;
  avg: number;
  projectedFromHistory: boolean;
}

/**
 * No-black-box payload behind a `resource_off_pod` signal: which data surfaced the
 * off-Pod contributor (forward Guidance, logged Actuals, or both) and the summed
 * hours from each source.
 */
export interface OffPodSignalBasis {
  kind: "resource_off_pod";
  source: "guidance" | "actuals" | "both";
  guidanceHours: number | null;
  actualsHours: number | null;
}

export type SignalBasis = IntensitySignalBasis | OffPodSignalBasis;

/** Narrow a signal's basis to the off-Pod shape (present only on resource_off_pod). */
export function isOffPodBasis(basis: SignalBasis | null | undefined): basis is OffPodSignalBasis {
  return !!basis && "kind" in basis && basis.kind === "resource_off_pod";
}

export interface SignalListRow {
  id: string;
  solutionId: string;
  solutionName: string;
  accountName: string;
  type: SignalType;
  currentIntensity: number | null;
  suggestedIntensity: number | null;
  currentPodSize: string | null;
  suggestedPodSize: string | null;
  suggestedSupportingPoints: number | null;
  /** The off-Pod Resource this signal is about (resource_off_pod only; null otherwise). */
  suggestedResourceId: string | null;
  suggestedResourceName: string | null;
  basis: SignalBasis;
  raisedAt: string;
  ageMs: number;
}

/** Placement chosen in the "Add Resource" dialog: a Primary Position or Supporting. */
export type AddResourcePlacement =
  | { role: "R1" | "R2" | "R3" | "R4" }
  | { supporting: true; points?: number };

export interface SignalFilter {
  type?: SignalType;
  accountId?: string;
}

/** Latest persisted suggestion for a Solution (drawer Signals tab). Always the
 *  Intensity suggestion — never an off-Pod basis. */
export interface SolutionSuggestion {
  suggestedIntensity: number;
  basis: IntensitySignalBasis;
  computedAt: string;
}

export interface SolutionSignalsPayload {
  signals: SignalListRow[];
  suggestion: SolutionSuggestion | null;
}

// ── Fetch helper (file-local, per frontend.md) ─────────────────────────────

async function jsonFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Request failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

// ── Query hooks ───────────────────────────────────────────────────────────

export function useSignals(filter: SignalFilter = {}) {
  const search = new URLSearchParams();
  if (filter.type) search.set("type", filter.type);
  if (filter.accountId) search.set("accountId", filter.accountId);
  const suffix = search.toString() ? `?${search.toString()}` : "";

  return useQuery({
    queryKey: signalKeys.list(filter),
    queryFn: () => jsonFetch<SignalListRow[]>(`/api/signals${suffix}`),
  });
}

/** Open signals + latest suggestion for ONE Solution — the drawer's Signals tab.
 *  Pass `enabled: false` (e.g. while the drawer is closed) to skip the fetch. */
export function useSolutionSignals(solutionId: string, enabled = true) {
  return useQuery({
    queryKey: signalKeys.forSolution(solutionId),
    queryFn: () => jsonFetch<SolutionSignalsPayload>(`/api/solutions/${solutionId}/signals`),
    enabled: enabled && solutionId.length > 0,
  });
}

// ── Mutation hooks ────────────────────────────────────────────────────────

export function useProcessSolution() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (solutionId: string) =>
      jsonFetch<{ suggestedIntensity: number }>(`/api/solutions/${solutionId}/process`, {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: signalKeys.all });
      toast.success("Solution re-processed.");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useConfirmIntensity() {
  const qc = useQueryClient();
  return useMutation({
    // One-click: the server derives the value from the signal's own computed
    // suggestedIntensity — no client-supplied number.
    mutationFn: (signalId: string) =>
      jsonFetch<{ ok: boolean }>(`/api/signals/${signalId}/confirm`, { method: "POST" }),
    onSuccess: () => {
      // Confirm writes Solution Intensity (+ Fit recalc) — refresh the open
      // drawer + dashboard views alongside the signals lists.
      qc.invalidateQueries({ queryKey: signalKeys.all });
      qc.invalidateQueries({ queryKey: solutionDetailKeys.all });
      qc.invalidateQueries({ queryKey: solutionDashboardKeys.all });
      qc.invalidateQueries({ queryKey: solutionWorkloadKeys.all });
      toast.success("Intensity confirmed.");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useApplyPodChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (signalId: string) =>
      jsonFetch<{ ok: boolean }>(`/api/signals/${signalId}/apply-pod`, { method: "POST" }),
    onSuccess: () => {
      // Apply-Pod writes Solution Pod Size (+ Fit recalc) — same refresh set.
      qc.invalidateQueries({ queryKey: signalKeys.all });
      qc.invalidateQueries({ queryKey: solutionDetailKeys.all });
      qc.invalidateQueries({ queryKey: solutionDashboardKeys.all });
      qc.invalidateQueries({ queryKey: solutionWorkloadKeys.all });
      toast.success("Pod change applied.");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

/**
 * "Commit both": confirm the suggested Intensity AND apply the suggested Pod
 * change in one action. Writes Solution Intensity + Pod Size (each with its own
 * Fit recalc) server-side, so refresh the same view set as the single-half actions.
 */
export function useConfirmIntensityAndPodChange() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (signalId: string) =>
      jsonFetch<{ ok: boolean }>(`/api/signals/${signalId}/confirm-and-apply-pod`, {
        method: "POST",
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: signalKeys.all });
      qc.invalidateQueries({ queryKey: solutionDetailKeys.all });
      qc.invalidateQueries({ queryKey: solutionDashboardKeys.all });
      qc.invalidateQueries({ queryKey: solutionWorkloadKeys.all });
      toast.success("Intensity and Pod change applied.");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

/** One-click dismiss — no reason required (dismissed signals stay audited by
 *  actor + timestamp). Posts no body. */
export function useDismissSignal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (signalId: string) =>
      jsonFetch<{ ok: boolean }>(`/api/signals/${signalId}/dismiss`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: signalKeys.all });
      toast.success("Signal dismissed.");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

/**
 * Apply a `resource_off_pod` signal by adding its Resource to the Pod at the
 * chosen placement (a Primary Position or Supporting). Writes an assignment (+
 * Fit recalc) server-side, so refresh the drawer / dashboard / workload / Pod
 * position views alongside the signals lists.
 */
export function useAddResourceFromSignal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      signalId: string;
      solutionId: string;
      placement: AddResourcePlacement;
    }) =>
      jsonFetch<{ ok: boolean }>(`/api/signals/${payload.signalId}/apply-add-resource`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          "role" in payload.placement
            ? { role: payload.placement.role }
            : { supporting: true, points: payload.placement.points },
        ),
      }),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: signalKeys.all });
      qc.invalidateQueries({ queryKey: solutionDetailKeys.all });
      qc.invalidateQueries({ queryKey: solutionDashboardKeys.all });
      qc.invalidateQueries({ queryKey: solutionWorkloadKeys.all });
      // The drawer keys its Pod positions on this literal tuple (SolutionDetailDrawer).
      qc.invalidateQueries({ queryKey: ["solution-pod-positions", variables.solutionId] });
      toast.success("Resource added to Pod.");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
